import type { ActiveMaintenanceBanner } from '../shell-hero/app-config.model';

const BANNER_TIME_ZONE = 'America/Toronto';
const dateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BANNER_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

interface BannerWindow {
  startsAt: Date | null;
  expiresAt: Date;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function utcTimestamp(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, 0);
  return date.getTime();
}

function getZonedDateTimeParts(timestamp: number): number[] {
  const parts = dateTimeFormatter.formatToParts(new Date(timestamp));
  return ['year', 'month', 'day', 'hour', 'minute', 'second'].map((type) =>
    Number(parts.find((part) => part.type === type)?.value),
  );
}

function parseEasternDateTime(value: unknown): Date | null {
  if (typeof value !== 'string') {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute, second] = match;
  const desiredParts = [year, month, day, hour, minute, second].map(Number);
  const wallTimeAsUtc = utcTimestamp(
    desiredParts[0],
    desiredParts[1],
    desiredParts[2],
    desiredParts[3],
    desiredParts[4],
    desiredParts[5],
  );
  const possibleOffsets = new Set<number>();

  for (let hours = -36; hours <= 36; hours += 6) {
    const probe = wallTimeAsUtc + hours * 60 * 60 * 1000;
    const zonedParts = getZonedDateTimeParts(probe);
    const offset =
      utcTimestamp(
        zonedParts[0],
        zonedParts[1],
        zonedParts[2],
        zonedParts[3],
        zonedParts[4],
        zonedParts[5],
      ) - probe;
    possibleOffsets.add(offset);
  }

  const matchingInstants = [...possibleOffsets]
    .map((offset) => wallTimeAsUtc - offset)
    .filter((instant) =>
      getZonedDateTimeParts(instant).every((part, index) => part === desiredParts[index]),
    )
    .sort((a, b) => a - b);

  // In the fall-back overlap, choose the earlier occurrence. Spring-forward gaps are invalid.
  return matchingInstants.length > 0 ? new Date(matchingInstants[0]) : null;
}

function getBannerWindow(config: unknown): BannerWindow | null {
  if (!isRecord(config) || config['enabled'] !== true) {
    return null;
  }

  const startsAtValue = config['startsAt'];
  if (startsAtValue !== null && startsAtValue !== undefined && typeof startsAtValue !== 'string') {
    return null;
  }

  const startsAt = startsAtValue == null ? null : parseEasternDateTime(startsAtValue);
  const expiresAt = parseEasternDateTime(config['expiresAt']);
  if ((startsAtValue != null && !startsAt) || !expiresAt) {
    return null;
  }

  if (startsAt && startsAt.getTime() >= expiresAt.getTime()) {
    return null;
  }

  return { startsAt, expiresAt };
}

export function resolveMaintenanceBanner(
  config: unknown,
  locale: 'en' | 'fr',
  now: Date,
): ActiveMaintenanceBanner | null {
  const bannerWindow = getBannerWindow(config);
  if (!bannerWindow) {
    return null;
  }

  const currentTime = now.getTime();
  if (
    (bannerWindow.startsAt && currentTime < bannerWindow.startsAt.getTime()) ||
    currentTime >= bannerWindow.expiresAt.getTime()
  ) {
    return null;
  }

  const content = isRecord(config) ? config['content'] : null;
  const localizedContent = isRecord(content) ? content[locale] : null;
  return typeof localizedContent === 'string' && localizedContent.trim()
    ? { html: localizedContent }
    : null;
}

export function nextMaintenanceBannerTransition(config: unknown, now: Date): number | null {
  const bannerWindow = getBannerWindow(config);
  if (!bannerWindow) {
    return null;
  }

  const currentTime = now.getTime();
  if (bannerWindow.startsAt && currentTime < bannerWindow.startsAt.getTime()) {
    return bannerWindow.startsAt.getTime();
  }

  return currentTime < bannerWindow.expiresAt.getTime() ? bannerWindow.expiresAt.getTime() : null;
}
